import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { Icon } from './Icon';
import { IconButton } from './IconButton';
import styles from './Toast.module.css';

type Tone = 'success' | 'info' | 'error';
type ToastItem = { id: number; title: string; message?: string; tone: Tone };
type ToastApi = { show: (toast: Omit<ToastItem, 'id' | 'tone'> & { tone?: Tone }) => void };

const ToastContext = createContext<ToastApi>({ show: () => undefined });
const glyphs = { success: CircleCheck, info: Info, error: CircleAlert };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const dismiss = useCallback(
    (id: number) => setItems((all) => all.filter((t) => t.id !== id)),
    [],
  );
  const show = useCallback<ToastApi['show']>(
    (toast) => {
      const id = Date.now() + Math.random();
      setItems((all) => [...all.slice(-2), { id, tone: 'success', ...toast }]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={styles.toast}>
            <span className={`${styles.glyph} ${styles[item.tone]}`}>
              <Icon icon={glyphs[item.tone]} size={22} />
            </span>
            <div className={styles.body}>
              <div className={styles.title}>{item.title}</div>
              {item.message && <div className={styles.message}>{item.message}</div>}
            </div>
            <IconButton
              icon={X}
              label="Dismiss"
              size={32}
              className={styles.close}
              onClick={() => dismiss(item.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
